<template>
    <transition name="fade">
      <div v-if="visible" class="fixed" @click="closePopup">
        <div class="bg-white" @click="($event) => $event.stopPropagation()">
          <h1 class="title">{{ props.purchaseData.purchase.codigo }}</h1>
          <div class="user-info">
              <img :src="purchaseData.user.foto" alt="Imagen del usuario" class="circular-image" />
              <div class="user-text">
                <h2 style="margin-bottom: 0px; margin-top: 0px;">{{ purchaseData.user.nombre }}</h2>
                <label>{{ purchaseData.user.email }}</label>
              </div>
            </div>
            <div class="questions" v-if="containsQuestions">
              <h3 style="margin-bottom: 0px; margin-top: 0px;">Información solicitada</h3>
              <label v-for="item in purchaseData.purchase.campos" :key="item.orden">
                <b>{{ item.pregunta }}:</b> {{ item.respuesta }}<br/>
              </label>
            </div>
            <div class="products">
              <h3 style="margin-bottom: 0px; margin-top: 0px;">Productos comprados</h3>
              <PurchasedProductDetails 
                v-for="product in purchaseData.purchase.productos"
                :key="product.id_usuario_producto"
                :status="purchaseData.purchase.estado"
                :product="product"
                :fecha_validez="purchaseData.purchase.fecha_validez"
                @update:quantity="quantityUpdated(product.id_usuario_producto, $event)"
                ref="productDetailsReferences"
                />
            </div>
            <button @click="selectAll" class="main_button" :class="selectAllClass" style="margin-top: 30px;">Seleccionar todo</button>
            <button @click="redeemProducts" class="main_button-green" :class="redeemButtonClass" style="margin-top: 10px;">Canjear</button>
        </div>
      </div>
    </transition>
  </template>
  
  <script setup>
  import { ref, watch, defineEmits, defineProps, computed } from 'vue';
  import PurchasedProductDetails from "@/components/common/PurchasedProductDetails.vue";
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    purchaseData: Object,
  });
  
  const visible = ref(false);
  const containsQuestions = ref(false);
  const products = ref(null);
  const productDetailsReferences = ref([]);
  const loadingStore = useLoadingStore();
  const emit = defineEmits(['errorRedeeming', 'successRedeeming']);
  const router = useRouter();

  watch(() => props.purchaseData, (newVal) => {
    visible.value = !!newVal;
    containsQuestions.value = newVal?.purchase?.campos.length > 0;
    products.value = newVal?.purchase?.productos;
  });
  
  const selectAllClass = computed(() => {
    var isEnabled = false;
    products.value.forEach(p => {
      if (p.cantidad > (p.cantidad_canjeada + (p.cantidad_para_canjear ?? 0))) {
        isEnabled = true;
      }
    });
    if (props.purchaseData.purchase.estado === 'Caducado') {
      isEnabled = false;
    }
    return isEnabled ? "button-enabled": "button-disabled";
  });

  const redeemButtonClass = computed(() => {
    var isEnabled = false;
    products.value.forEach(p => {
      if (p.cantidad_para_canjear > 0)
        isEnabled = true;
      }
    );
    return isEnabled ? "button-enabled": "button-disabled";
  });

  const quantityUpdated = (productId, quantity) => {
    products.value.find(p => p.id_usuario_producto === productId).cantidad_para_canjear = quantity;
  };

  const closePopup = () => {
    visible.value = false;
    history.back();
  };

  const selectAll = () => {
    if (props.purchaseData.purchase.estado !== 'Caducado') {
      productDetailsReferences.value.forEach(p => p.selectAllExecuted());
    }
  }

const redeemProducts = async () => {
  var products = [];
  props.purchaseData.purchase.productos.forEach(p => {
    if (p.cantidad_para_canjear ?? 0 > 0) {
      products.push({
        "id_usuario_producto": p.id_usuario_producto,
        "cantidad": p.cantidad_para_canjear ?? 0
      });
    }
  });
  if (products.length === 0) {
    return;
  }
  loadingStore.setLoading(true);
  try {
    let response = await fetchRedeemProducts(props.purchaseData.purchase.codigo, products);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchRedeemProducts(props.purchaseData.purchase.codigo, products);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      emit('errorRedeeming', "Se ha producido un error canjeando la reserva.");
    }

    const data = await response.json();

    if (data.errors) {
      loadingStore.setLoading(false);
      emit('errorRedeeming', data.message);
    } else {
      loadingStore.setLoading(false);
      emit('successRedeeming');
    }
  } catch (exception) {
    loadingStore.setLoading(false);
    emit('errorRedeeming', exception.message);
  } finally {
    visible.value = false;
    history.back();
  }
};

async function fetchRedeemProducts(codigo, products) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/redeems/redeem_purchase_code", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "purchase_code": codigo,
        "products": products
      }),
    });
}

  </script>
  
  <style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  
  .fixed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 10;
  }
  
  .bg-white {
    background-color: white;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    width: 80%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .circular-image {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 20px;
  }

  .user-info {
    display: flex;
    align-items: center;
  }

  .user-text {
    display: flex;
    flex-direction: column;
  }

  .user-text h2 {
    width: 100%;
  }
  .questions {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--very-clear-gray);
    padding-top: 15px;
  }

  .questions h3 {
    font-weight: normal;
  }

  .questions label {
    margin-top: 10px;
  }

  .products {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--very-clear-gray);
    padding-top: 15px;
  }

  .products h3 {
    font-weight: normal;
  }

  .button-disabled {
    opacity: 0.5;
  }

  .button-enabled {
    opacity: 1;
  }

  .title {
    border-bottom: 1px solid var(--very-clear-gray);
    text-align: center;
    margin-top: 0px;
  }

  </style>
  